import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

interface ScheduleRow {
  start: string;
  end: string;
  activity: string;
  highlight?: boolean; // main competition events
}

const SATURDAY: ScheduleRow[] = [
  { start: '09:00', end: '10:00', activity: 'Registration' },
  { start: '10:00', end: '12:00', activity: 'Beginner Climbing Lesson / Warm Up' },
  { start: '11:30', end: '12:00', activity: 'Briefing Lead & Speed' },
  { start: '12:00', end: '16:00', activity: 'Para Climbing Lead & Top-rope Qualification', highlight: true },
  { start: '16:15', end: '17:00', activity: 'Para Climbing Speed Qualification', highlight: true },
  { start: '17:15', end: '18:10', activity: '⚡ Para Climbing Speed Finals', highlight: true },
  { start: '18:00', end: '19:00', activity: '🍖 BBQ Dinner', highlight: true },
  { start: '18:40', end: '18:50', activity: 'Athlete Presentations', highlight: true },
  { start: '18:50', end: '18:56', activity: 'Route Reading', highlight: true },
  { start: '19:00', end: '20:30', activity: '🧗 Para Climbing Lead & Top-rope Finals', highlight: true },
  { start: '20:30', end: '21:00', activity: '🏆 Podium Lead & Speed', highlight: true },
  { start: '21:00', end: '—',     activity: '🔥 Drinks & Fire', highlight: true },
];

const SUNDAY: ScheduleRow[] = [
  { start: '09:00', end: '11:00', activity: 'Warm Up & Registrations', highlight: true },
  { start: '10:30', end: '10:45', activity: 'Briefing', highlight: true },
  { start: '11:00', end: '14:00', activity: '🪨 Para Climbing Boulder Competition', highlight: true },
  { start: '14:00', end: '14:30', activity: '🏆 Podium Boulder', highlight: true },
  { start: '15:00', end: '19:00', activity: 'Venue Breakdown', highlight: true },
];

const DaySection: React.FC<{ day: string; date: string; rows: ScheduleRow[]; accent: string }> = ({
  day, date, rows, accent,
}) => (
  <section className="mb-8">
    <div className={`${accent} text-white px-5 py-4 rounded-t-xl`}>
      <h2 className="text-2xl font-extrabold tracking-tight">{day}</h2>
      <p className="text-white/70 text-sm mt-0.5">{date}</p>
    </div>
    <div className="bg-white rounded-b-xl shadow overflow-hidden divide-y divide-gray-100">
      {rows.map((row, i) => (
        <div
          key={i}
          className={`flex items-start gap-4 px-5 py-3 ${
            row.highlight
              ? 'bg-white'
              : 'bg-gray-50'
          }`}
        >
          {/* Time column */}
          <div className="w-28 shrink-0 text-right">
            <span className={`font-mono text-sm font-bold ${row.highlight ? 'text-secondary' : 'text-gray-400'}`}>
              {row.start}
            </span>
            {row.end !== '—' && (
              <span className="font-mono text-xs text-gray-300 block">→ {row.end}</span>
            )}
          </div>
          {/* Accent bar */}
          <div className={`w-1 self-stretch rounded-full shrink-0 ${row.highlight ? 'bg-secondary' : 'bg-gray-200'}`} />
          {/* Activity */}
          <p className={`py-0.5 text-sm leading-snug ${row.highlight ? 'font-semibold text-dark' : 'text-gray-500'}`}>
            {row.activity}
          </p>
        </div>
      ))}
    </div>
  </section>
);

const SchedulePage: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();

  return (
    <div className="min-h-screen bg-dark text-white">
      {/* Hero */}
      <div className="px-4 pt-10 pb-6">
        <div className="max-w-2xl mx-auto">
          <button
            onClick={() => navigate('/results')}
            className="flex items-center gap-1.5 text-blue-300 hover:text-white text-sm mb-6 transition-colors"
          >
            {t('schedulePage.back')}
          </button>
          <h1 className="text-4xl font-extrabold tracking-tight mb-1">{t('schedulePage.title')}</h1>
          <p className="text-blue-300 text-lg">{t('schedulePage.subtitle')}</p>
        </div>
      </div>

      {/* Day sections */}
      <div className="max-w-2xl mx-auto px-4 pb-16">
        <DaySection
          day={t('schedulePage.saturday')}
          date={t('schedulePage.saturdayDate')}
          rows={SATURDAY}
          accent="bg-secondary"
        />
        <DaySection
          day={t('schedulePage.sunday')}
          date={t('schedulePage.sundayDate')}
          rows={SUNDAY}
          accent="bg-primary"
        />
      </div>
    </div>
  );
};

export default SchedulePage;
